import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, Edit, Eye, Trash2, Plus, Search, ChevronLeft, ChevronRight, Ticket, Filter, LogOut } from 'lucide-react';

const EventsListV2 = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, free, paid
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9;

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events`);
            const data = await response.json();
            if (data.success) {
                setEvents(data.data);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('Are you sure you want to delete this event?')) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}`, {
                method: 'DELETE',
            });
            const data = await response.json();
            if (data.success) {
                setEvents(events.filter(e => e._id !== eventId));
            }
        } catch (error) {
            console.error('Error deleting event:', error);
        }
    };

    // Filter Logic
    const filteredEvents = events.filter(event => {
        const matchesFilter = filter === 'all' || event.eventType === filter;
        const matchesSearch = event.eventName.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    // Pagination Logic
    const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);
    const paginatedEvents = filteredEvents.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filter, searchTerm]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading events...</p>
                </div>
            </div>
        );
    }

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("email");
        localStorage.removeItem("type");
        localStorage.removeItem("eventId");
        localStorage.removeItem("eventName");
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            {/* App Header */}
            <header className="sticky top-0 z-50 w-full bg-gradient-to-r from-white via-gray-50 to-slate-100 backdrop-blur-md border-b border-gray-200/50 shadow-lg">
                <div className="flex h-16 mx-auto items-center justify-between px-4 lg:px-[80px]">
                    <a
                        className="group flex items-center gap-3 hover:scale-105 transition-all duration-300 cursor-pointer"
                        onClick={() => navigate("/v2/events")}
                        rel="ugc"
                    >
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-700 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-blue-500/25 transition-all duration-300">
                            <Calendar className="h-5 w-5 text-white" />
                        </div>
                        <span className="font-bold hidden cursor-pointer lg:block text-lg bg-gradient-to-r from-gray-800 to-slate-700 bg-clip-text text-transparent">
                            Event ID Card Generator App
                        </span>
                    </a>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/event')}
                            className="flex items-center gap-1.5 py-2 px-3 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-semibold transition-colors duration-200"
                        >
                            <span>V1 Events</span>
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" /></svg>
                        </button>

                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-1.5 py-2 px-3 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-semibold transition-colors duration-200"
                        >
                            <LogOut className="w-4 h-4" />
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Events</h1>
                        <p className="text-gray-500 mt-1">Manage and organize your events</p>
                    </div>
                    <button
                        onClick={() => navigate('/v2/events/create')}
                        className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Create Event
                    </button>
                </div>

                {/* Filters and Search Bar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-8">
                    <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                        {/* Event Type Tabs */}
                        <div className="flex p-1 bg-gray-100/50 rounded-lg w-full md:w-auto">
                            {['all', 'free', 'paid'].map((type) => (
                                <button
                                    key={type}
                                    onClick={() => setFilter(type)}
                                    className={`flex-1 md:flex-none px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 capitalize ${filter === type
                                        ? 'bg-white text-blue-600 shadow-sm ring-1 ring-gray-200'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
                                        }`}
                                >
                                    {type}
                                    <span className={`ml-2 text-xs py-0.5 px-1.5 rounded-full ${filter === type ? 'bg-blue-50 text-blue-700' : 'bg-gray-200 text-gray-600'
                                        }`}>
                                        {events.filter(e => type === 'all' || e.eventType === type).length}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Search Input */}
                        <div className="relative w-full md:w-80">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search events..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all"
                            />
                        </div>
                    </div>
                </div>

                {/* Events Grid */}
                {filteredEvents.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Calendar className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">No events found</h3>
                        <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                            {searchTerm ? `No events matching "${searchTerm}"` : "Get started by creating your first event to manage registrations and ID cards."}
                        </p>
                        <button
                            onClick={() => navigate('/v2/events/create')}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Event
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {paginatedEvents.map((event) => (
                                <EventCard
                                    key={event._id}
                                    event={event}
                                    onEdit={() => navigate(`/v2/events/${event._id}/edit`)}
                                    onView={() => navigate(`/v2/events/${event._id}`)}
                                    onDelete={() => handleDeleteEvent(event._id)}
                                />
                            ))}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-8 rounded-lg shadow-sm">
                                <div className="flex flex-1 justify-between sm:hidden">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm text-gray-700">
                                            Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                                            <span className="font-medium">
                                                {Math.min(currentPage * itemsPerPage, filteredEvents.length)}
                                            </span>{' '}
                                            of <span className="font-medium">{filteredEvents.length}</span> results
                                        </p>
                                    </div>
                                    <div>
                                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <span className="sr-only">Previous</span>
                                                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                                            </button>
                                            {/* Page Numbers */}
                                            {[...Array(totalPages)].map((_, i) => (
                                                <button
                                                    key={i + 1}
                                                    onClick={() => handlePageChange(i + 1)}
                                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === i + 1
                                                        ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                                        }`}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <span className="sr-only">Next</span>
                                                <ChevronRight className="h-5 w-5" aria-hidden="true" />
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// Enhanced Event Card Component with Image
const EventCard = ({ event, onEdit, onView, onDelete }) => {
    const [showActions, setShowActions] = useState(false);

    const formatDate = (dateString) => {
        if (!dateString) return 'Date not set';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    // Determined Image Source
    // Priority: event.coverImage > event.photoUrl > Random Fallback
    const imageSource = event.coverImage || event.photoUrl || "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80";

    return (
        <div
            className="group bg-white rounded-xl border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col h-full"
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
        >
            {/* Image Header */}
            <div className="relative h-48 overflow-hidden bg-gray-100">
                <img
                    src={imageSource}
                    alt={event.eventName}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?auto=format&fit=crop&w=1000&q=80"; }} // Fallback on error
                />

                {/* Overlay Badge */}
                <div className="absolute top-3 right-3 flex space-x-2">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full shadow-sm backdrop-blur-md ${event.eventType === 'free'
                            ? 'bg-green-500/90 text-white'
                            : 'bg-indigo-500/90 text-white'
                        }`}>
                        {event.eventType === 'free' ? 'Free' : 'Paid'}
                    </span>
                </div>
            </div>

            {/* Content */}
            <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
                    {event.eventName}
                </h3>

                <div className="space-y-2.5 mt-auto">
                    <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-2.5 text-gray-400" />
                        <span className="truncate font-medium">
                            {formatDate(event.eventStartDate)}
                            {event.eventEndDate && event.eventStartDate !== event.eventEndDate && ` - ${formatDate(event.eventEndDate)}`}
                        </span>
                    </div>

                    <div className="flex items-center text-sm text-gray-500">
                        <Users className="w-4 h-4 mr-2.5 text-gray-400" />
                        <span className="font-medium">{event.participantCount || 0} Participants</span>
                    </div>

                    {event.eventType === 'paid' && (
                        <div className="flex items-center text-sm text-gray-500">
                            <Ticket className="w-4 h-4 mr-2.5 text-gray-400" />
                            <span className="font-medium">{event.tickets?.length || 0} Ticket Types</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Actions Footer - Always visible on mobile/tablet, fade in on desktop */}
            <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-3">
                <div className="flex items-center justify-between gap-2">
                    <button
                        onClick={(e) => { e.stopPropagation(); onView(); }}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                        <Eye className="w-4 h-4 mr-1.5" />
                        View
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(); }}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                    >
                        <Edit className="w-4 h-4 mr-1.5" />
                        Edit
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="inline-flex items-center justify-center p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Event"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EventsListV2;